// ✅ src/pages/VehicleV2/DayWiseLubeConsumptionReport_Print.jsx
// React PRINT page for: DayWiseLubeConsumptionReportrPrint.aspx (Crystal print)
// - Reads filters from URL query string
// - Calls backend: GET https://nissi-india.com/api/dayWiseLubeConsumptionReport/print
// - Builds dynamic day columns 1..totalDays
// - Highlights > 1 values in red bold (like ASPX RowDataBound logic)
// - Adds footer totals for each day + grand total
// - A4 landscape print CSS (better for 31 columns)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/daywiselubeConsumptionReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// detects day columns coming from API row: "1","2"...,"31" (string keys)
const getDayValue = (row, day) => {
  // try both numeric and string keys
  const k1 = String(day);
  return row?.[k1] ?? row?.[day] ?? row?.[`[${day}]`] ?? "";
};

export default function DayWiseLubeConsumptionReportPrint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ Read from query string (send these from list page when opening print)
  // Example URL:
  // /vehicle/DayWiseLubeConsumptionReport_Print?
  //   vehType=O&branchId=Select&month=February&year=2026&
  //   vehRegNo=&vehCode=&vehName=&vehicleMainTypeId=Select&
  //   itemCategoryId=37&itemId=123&itemName=ENGINE%20OIL
  const params = useMemo(() => {
    const obj = Object.fromEntries([...searchParams.entries()]);
    return {
      vehType: (obj.vehType || "O").toUpperCase() === "H" ? "H" : "O",
      branchId: obj.branchId || "Select",
      month: obj.month || "",
      year: obj.year || "",
      vehRegNo: obj.vehRegNo || "",
      vehCode: obj.vehCode || "",
      vehName: obj.vehName || "",
      vehicleMainTypeId: obj.vehicleMainTypeId || "Select",
      itemCategoryId: obj.itemCategoryId || "Select",
      itemId: obj.itemId || "Select",
      itemName: obj.itemName || "",
    };
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [payload, setPayload] = useState({
    title: "DAY WISE LUBE CONSUMPTION REPORT",
    totalDays: 31,
    rows: [],
  });

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        // ✅ recommended endpoint
        const res = await axios.get(`${API_BASE}/print`, { params });
        const d = res?.data || {};
        if (!alive) return;

        if (!d.success) throw new Error(d.message || "Failed");

        setPayload({
          title: d.title || "DAY WISE LUBE CONSUMPTION REPORT",
          totalDays: Number(d.totalDays || 31),
          rows: Array.isArray(d.rows) ? d.rows : [],
        });
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  const totalDays = useMemo(() => {
    const n = Number(payload.totalDays || 31);
    return Number.isFinite(n) && n > 0 ? n : 31;
  }, [payload.totalDays]);

  // ✅ normalize base columns (your API rows likely have these names)
  const rows = useMemo(() => {
    const list = payload.rows || [];
    return list.map((r, idx) => {
      // try common aliases
      const branch = r.Branch ?? r.fld_BranchName ?? r.branch ?? "";
      const mainType = r.VehicleMainType ?? r.fld_vehiclemaintype ?? r.vehicleMainType ?? "";
      const vCode = r.VehicleCode ?? r.fld_vehicle_code ?? r.vehicleCode ?? "";
      const regNo = r.VehicleRegNo ?? r.fld_vehicle_regno ?? r.vehicleRegNo ?? "";

      return {
        _raw: r,
        sno: idx + 1,
        Branch: branch,
        VehicleMainType: mainType,
        VehicleCode: vCode,
        VehicleRegNo: regNo,
      };
    });
  }, [payload.rows]);

  // ✅ footer totals per day + grand total
  const dayTotals = useMemo(() => {
    const totals = Array.from({ length: totalDays }, () => 0);
    for (const r of payload.rows || []) {
      for (let d = 1; d <= totalDays; d++) {
        totals[d - 1] += toNum(getDayValue(r, d));
      }
    }
    return totals;
  }, [payload.rows, totalDays]);

  const grandTotal = useMemo(() => {
    // prefer API Total column if exists, else sum dayTotals
    let sum = 0;
    const list = payload.rows || [];
    const hasTotal = list.some((r) => r.Total !== undefined);
    if (hasTotal) {
      sum = list.reduce((acc, r) => acc + toNum(r.Total), 0);
    } else {
      sum = dayTotals.reduce((acc, n) => acc + n, 0);
    }
    return sum;
  }, [payload.rows, dayTotals]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 6px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7.5pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page {
          size: A4 landscape;
          margin: 0.30in;
        }
      `}</style>

      <div className="p-2 print:p-1">
        {/* Top Actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>

          <div className="flex gap-2">
            {/* Crystal had MAIL button. You can connect later to /exportPdf */}
            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
              </div>
            </div>

            <div className="flex-1 text-center leading-tight">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi CIVIL CONSTRUCTION</div>
              <div className="text-[10px] mt-1">
                3/42-12, Nagappa Gounder St, kavundampalayam, Coimbatore - Tamil Nadu.
                <br />
                Phone No : 9003744644 &nbsp;&nbsp; infracosoft-implementa
              </div>

              <div className="text-sm font-bold underline mt-2">{payload.title}</div>
              <div className="text-[10px] mt-1">
                Month: <b>{toStr(params.month) || "-"}</b> &nbsp; Year: <b>{toStr(params.year) || "-"}</b>
                &nbsp; Item: <b>{toStr(params.itemName) || "-"}</b>
              </div>
            </div>

            <div className="w-44 text-right text-[10px]">
              <div>INFRA SCREEN : B-59/1</div>
              <div>
                Vehicle Type: <b>{params.vehType === "H" ? "Hire" : "Own"}</b>
              </div>
              <div>
                Branch: <b>{toStr(params.branchId) || "-"}</b>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 35 }}>
                  S.No
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 110 }}>
                  Branch
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 120 }}>
                  Vehicle Main Type
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 75 }}>
                  Vehicle Code
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 85 }}>
                  Reg No.
                </th>

                {/* Dynamic day columns */}
                {Array.from({ length: totalDays }, (_, i) => {
                  const day = i + 1;
                  return (
                    <th
                      key={`h-${day}`}
                      className="border border-black px-1 py-1 text-center"
                      style={{ minWidth: 28 }}
                    >
                      {day}
                    </th>
                  );
                })}

                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 60 }}>
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {!payload.rows?.length ? (
                <tr>
                  <td colSpan={6 + totalDays} className="border border-black px-2 py-2 text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const raw = r._raw;
                  const rowTotal = raw?.Total !== undefined ? toNum(raw.Total) : null;

                  return (
                    <tr key={`${r.VehicleCode}-${r.sno}`}>
                      <td className="border border-black px-1 py-1 text-center">{r.sno}</td>
                      <td className="border border-black px-1 py-1">{r.Branch}</td>
                      <td className="border border-black px-1 py-1">{r.VehicleMainType}</td>
                      <td className="border border-black px-1 py-1 text-center">{r.VehicleCode}</td>
                      <td className="border border-black px-1 py-1 text-center">{r.VehicleRegNo}</td>

                      {Array.from({ length: totalDays }, (_, i) => {
                        const day = i + 1;
                        const v = getDayValue(raw, day);
                        const n = toNum(v);
                        const alert = n > 1; // like ASPX "gg > 1" red bold

                        return (
                          <td
                            key={`c-${r.sno}-${day}`}
                            className="border border-black px-1 py-1 text-right"
                            style={alert ? { color: "red", fontWeight: "bold" } : undefined}
                          >
                            {v === null || v === undefined || v === "" ? "" : fmtNum2(v)}
                          </td>
                        );
                      })}

                      <td className="border border-black px-1 py-1 text-right font-bold">
                        {rowTotal !== null ? fmtNum2(rowTotal) : fmtNum2(
                          Array.from({ length: totalDays }, (_, i) => toNum(getDayValue(raw, i + 1)))
                            .reduce((a, b) => a + b, 0)
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Footer totals */}
            {payload.rows?.length ? (
              <tfoot>
                <tr>
                  <td colSpan={5} className="border border-black px-2 py-1 text-center font-bold">
                    Total
                  </td>

                  {dayTotals.map((t, idx) => (
                    <td key={`ft-${idx + 1}`} className="border border-black px-1 py-1 text-right font-bold">
                      {t ? fmtNum2(t) : ""}
                    </td>
                  ))}

                  <td className="border border-black px-1 py-1 text-right font-bold">
                    {fmtNum2(grandTotal)}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>

        {/* Footer note */}
        <div className="mt-3 border-2 border-black p-1 text-xs text-center">
          Report Created by Infracosoft version 2.1. www.infracosoft.com
        </div>

        {/* Bottom Print Button */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}